import './customEllipsisText.scss';

import React, { useMemo, useRef, useState } from 'react';

import Linkify from 'react-linkify';

import { mentionRegex } from '../../shared/communityUtils';

type Props = {
  text: string;
  charLimit?: number;
  textClassName?: string;
  seeMoreBtnClassName?: string;
  customDisplay?: (text: string) => React.ReactNode;
};

const CustomEllipsisText: React.FC<Props> = ({
  text,
  charLimit = 30,
  textClassName,
  seeMoreBtnClassName,
  customDisplay,
}) => {
  const [showTrimmedText, setShowTrimmedText] = useState<boolean>(true);
  const [showSeeMoreButton, setShowSeeMoreButtom] = useState<boolean>(false);

  const textRef = useRef<HTMLSpanElement | null>(null);

  const displyText = useMemo(() => {
    const splitArray = text.split(' ');
    if (text?.length <= charLimit || splitArray?.length === 1) {
      setShowSeeMoreButtom(false);
      return text;
    }
    if (!showTrimmedText) return text;

    const tagsRegexResult = `${text}`.match(mentionRegex);
    const tags: string[] = [];
    tagsRegexResult?.forEach((element) => {
      tags.push(element);
    });

    const splits = text.split(mentionRegex);

    const splitTextWitTags = splits.reduce(
      (acc: string[], eachStr: string, index: number) => {
        const currSplits = eachStr?.split(' ');
        const requiredTag = tags?.[index] || '';
        return [...acc, ...currSplits, requiredTag];
      },
      [],
    );

    let strLength = 0;
    let displayString = '';
    let lastIndex = 0;
    for (let index = 0; index < splitTextWitTags.length; index++) {
      const eachString = splitTextWitTags[index];
      displayString = displayString + ' ' + eachString;
      lastIndex = index;
      strLength = strLength + eachString?.length;
      if (strLength >= charLimit) {
        break;
      }
    }

    const leftOverStr = splitTextWitTags.slice(lastIndex).join('');
    if (leftOverStr?.length > charLimit) {
      setShowSeeMoreButtom(true);
      return `${displayString}...`;
    }
    setShowSeeMoreButtom(false);
    return `${text}   `;
  }, [charLimit, showTrimmedText, text]);

  const handleSeeMore = () => {
    if (!showTrimmedText) {
      textRef?.current?.scrollIntoView();
    }
    setShowTrimmedText((prev) => !prev);
  };

  const renderText = (textToRender: string) => {
    const lines = textToRender.split('\n');
    return (
      <span style={{ whiteSpace: 'pre-wrap' }}>
        {lines.map((line, index) => (
          <React.Fragment key={index}>
            <Linkify>{line}</Linkify>
            {index < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </span>
    );
  };

  return (
    <>
      {customDisplay ? (
        customDisplay(displyText)
      ) : (
        <span
          className={`${textClassName} ellipsis-text`}
          ref={(ref) => (textRef.current = ref)}>
          {renderText(displyText)}
        </span>
      )}
      {showSeeMoreButton ? (
        <button
          className={`ellipsis-button ${seeMoreBtnClassName}`}
          onClick={handleSeeMore}>
          {showTrimmedText ? 'Read more' : 'Read less'}
        </button>
      ) : null}
    </>
  );
};

export default CustomEllipsisText;
